import { fillArray } from '../common/util';
import { Pattern } from './patterns';

const GRID_WIDTH = 50;
const GRID_HEIGHT = 40;

const newPerson = () => (Math.random() < 0.3);
const populate = (n: number) => fillArray(n, newPerson)

export class Population {

  public static empty(width: number, height: number): Population {
    const pop = fillArray(width * height, () => false);
    return new Population(width, height, pop);
  }

  public readonly HEIGHT: number;
  public readonly WIDTH: number;

  private readonly People: Array<boolean>;

  constructor(width: number, height: number, people?: Array<boolean>) {
    this.HEIGHT = height;
    this.WIDTH = width;
    if (!people) {
      people = populate(width * height);
    }
    this.People = people;
  }

  public at(x: number, y: number): boolean {
    if (x < 0) {
      x += this.WIDTH;
    } else if (x >= this.WIDTH) {
      x -= this.WIDTH;
    }
    if (y < 0) {
      y += this.HEIGHT;
    } else if (y >= this.HEIGHT) {
      y -= this.HEIGHT;
    }
    return this.People[this.WIDTH * y + x];
  }

  public clear() {
    this.People.forEach((p, i) => { this.People[i] = false; }) }

  public get count() {
    return this.People.reduce((c, p) => c + (p ? 1 : 0), 0);
  }

  public evolve(): Population {
    const newPop = this.People.map((p, i) => this.doom(i));
    return new Population(this.WIDTH, this.HEIGHT, newPop);
  }

  public index(i: number): boolean {
    return this.People[i];
  }

  public indexToXY = (i: number) =>
    [i % this.WIDTH, Math.floor(i / this.WIDTH)];

  public place(pattern: Pattern): Population {
    const patternHeight = pattern.length;
    const patternWidth = Math.max(...pattern.map(a => a.length));
    const pop = new Population(this.WIDTH, this.HEIGHT, [...this.People]);
    const x = Math.floor(this.WIDTH / 2) - Math.ceil(patternWidth / 2) - 1;
    const y = Math.floor(this.HEIGHT / 2) - Math.ceil(patternHeight / 2) - 1;

    pattern.forEach((row, j) => {
      for (let i = 0; i < row.length; i++) {
        if (row[i] !== ' ') {
          pop.set(x + i, y + j);
        }
      }
    })

    return pop;
  }

  public toggleCell(x: number, y: number): Population {
    const pop = new Population(this.WIDTH, this.HEIGHT, [...this.People]);
    pop.set(x, y, !pop.at(x, y));
    return pop;
  }

  // "doom" in the original Old English sense of fate or judgement
  private doom(i: number) {
    const [x, y] = this.indexToXY(i);
    const me = this.index(i);

    const neighbors = [
      // above
      this.at(x - 1, y - 1),
      this.at(x,     y - 1),
      this.at(x + 1, y - 1),
      // next door
      this.at(x - 1, y),
      this.at(x + 1, y),
      // below
      this.at(x - 1, y + 1),
      this.at(x    , y + 1),
      this.at(x + 1, y + 1),
    ];

    const count = neighbors.filter(p => p).length;

    if (me) {
      // if alive, survives if 2-3 neighbors
      return (count === 2 || count === 3);
    } else {
      // f dead, revive if 3+ neighbors
      return (count === 3);
    }

  }

  private set(x: number, y: number, value: boolean = true): void {
    this.People[y * this.WIDTH + x] = value;
  }

}

export interface GameState {
  population: Population,
  rate: number,
  suspended: boolean,
  year: number,
}

export const initializeState = (): GameState => {
  const population = Population.empty(GRID_WIDTH, GRID_HEIGHT);

  return {
    population,
    rate: 100,
    suspended: true,
    year: 0,
  };
}



// WEBPACK FOOTER //
// ./src/common/life.ts